/**
 * Makes filtering, sorting, and "Show more" pagination on the collection
 * page genuinely async via Shopify's Section Rendering API, instead of full
 * page reloads: submitting a facet/sort form or clicking "Show more" fetches
 * `?section_id=<this section>` in the background, swaps the rendered result
 * in, and updates the URL with history.pushState - so a slow or failed
 * request has something real to show (the skeleton actually reflects a
 * network wait, and a failure gets a genuine error/retry state) rather than
 * decorative markup with nothing behind it.
 *
 * DOM contract: the section root carries `data-section-id`. Inside it:
 * `[data-collection-grid]` (results + pagination), `[data-collection-loading]`
 * (skeleton), and `[data-collection-error]` (error banner with a
 * `[data-collection-retry]` button) are siblings that get shown/hidden by
 * this script. `form`s inside `.collection-page__toolbar` /
 * `.collection-page__filters` / `.collection-page__mobile-controls`, and any
 * `[data-pagination-next]` link, are intercepted via delegated document
 * listeners (not per-element bindings) so they keep working after the
 * section root is replaced wholesale by a fetch response. "Show more" appends
 * the fetched grid's product cards to the existing grid instead of replacing
 * it; filter/sort requests replace the whole section (facet counts, active
 * chips, and the grid must all update together from one consistent render).
 */
let lastFailedUrl = null;

function collectionRoot() {
  return document.querySelector('.collection-page[data-section-id]');
}

function withSectionId(url, sectionId) {
  const joined = new URL(url, window.location.origin);
  joined.searchParams.set('section_id', sectionId);
  return joined.toString();
}

function setLoading(root, isLoading) {
  const grid = root.querySelector('[data-collection-grid]');
  const skeleton = root.querySelector('[data-collection-loading]');
  const errorBanner = root.querySelector('[data-collection-error]');
  if (grid) grid.hidden = isLoading;
  if (skeleton) skeleton.hidden = !isLoading;
  if (isLoading && errorBanner) errorBanner.hidden = true;
}

function showError(root, failedUrl) {
  lastFailedUrl = failedUrl;
  const grid = root.querySelector('[data-collection-grid]');
  const skeleton = root.querySelector('[data-collection-loading]');
  const errorBanner = root.querySelector('[data-collection-error]');
  if (grid) grid.hidden = true;
  if (skeleton) skeleton.hidden = true;
  if (errorBanner) errorBanner.hidden = false;
}

async function replaceSection(navigationUrl) {
  const root = collectionRoot();
  if (!root) return;
  setLoading(root, true);

  try {
    const response = await fetch(withSectionId(navigationUrl, root.dataset.sectionId));
    if (!response.ok) throw new Error(`${response.status}`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = parsed.querySelector('.collection-page[data-section-id]');
    if (!nextRoot) throw new Error('missing-section-root');

    root.replaceWith(nextRoot);
    window.history.pushState({ vellumCollection: true }, '', navigationUrl);
    window.dispatchEvent(new CustomEvent('vellum:cards-updated'));
  } catch (error) {
    showError(root, navigationUrl);
  }
}

async function appendNextPage(nextUrl) {
  const root = collectionRoot();
  if (!root) return;
  const grid = root.querySelector('[data-collection-grid]');
  const pagination = grid?.querySelector('.collection-page__pagination');
  const showMoreLink = pagination?.querySelector('[data-pagination-next]');

  if (showMoreLink) {
    showMoreLink.setAttribute('aria-busy', 'true');
    showMoreLink.dataset.originalText = showMoreLink.dataset.originalText || showMoreLink.textContent;
    showMoreLink.textContent = showMoreLink.dataset.loadingText || showMoreLink.textContent;
  }

  try {
    const response = await fetch(withSectionId(nextUrl, root.dataset.sectionId));
    if (!response.ok) throw new Error(`${response.status}`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextGrid = parsed.querySelector('[data-collection-grid] .collection-page__grid');
    const nextPagination = parsed.querySelector('.collection-page__pagination');
    const currentGrid = grid.querySelector('.collection-page__grid');

    if (currentGrid && nextGrid) {
      currentGrid.append(...nextGrid.children);
    }
    if (pagination) {
      if (nextPagination) {
        pagination.replaceWith(nextPagination);
      } else {
        pagination.remove();
      }
    }
    window.history.pushState({ vellumCollection: true }, '', nextUrl);
    window.dispatchEvent(new CustomEvent('vellum:cards-updated'));
  } catch (error) {
    if (showMoreLink) {
      showMoreLink.removeAttribute('aria-busy');
      showMoreLink.textContent = showMoreLink.dataset.originalText || showMoreLink.textContent;
    }
    showError(root, nextUrl);
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest(
    '.collection-page__toolbar form, .collection-page__filters form, .collection-page__mobile-controls form'
  );
  if (!form) return;

  event.preventDefault();
  const params = new URLSearchParams(new FormData(form));
  const action = form.getAttribute('action') || window.location.pathname;
  replaceSection(`${action}?${params.toString()}`);
});

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-pagination-next]');
  if (!link) return;

  event.preventDefault();
  appendNextPage(link.href);
});

document.addEventListener('click', (event) => {
  const retryButton = event.target.closest('[data-collection-retry]');
  if (!retryButton || !lastFailedUrl) return;

  replaceSection(lastFailedUrl);
});

window.addEventListener('popstate', () => {
  if (collectionRoot()) replaceSection(window.location.href);
});
